export type BookedSpot = {
  spot_name: string;
  guest: number;
};

function normalizeSpotName(spotName: string): string | null {
  const trimmed = spotName.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isBookedSpot(value: unknown): value is BookedSpot {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record['spot_name'] === 'string' && typeof record['guest'] === 'number';
}

export function normalizeBookedSpots(raw: unknown): BookedSpot[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((entry) => {
    if (!isBookedSpot(entry)) {
      return [];
    }
    const spot_name = normalizeSpotName(entry.spot_name);
    if (!spot_name || !Number.isFinite(entry.guest)) {
      return [];
    }
    return [{ spot_name, guest: entry.guest }];
  });
}

/** Like normalizeBookedSpots but keeps an empty member slot (`guest === 0`) for buddy wire shape. */
export function normalizeBookedSpotsPreservingEmptyMemberSlot(raw: unknown): BookedSpot[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((entry) => {
    if (!isBookedSpot(entry)) {
      return [];
    }
    if (!Number.isFinite(entry.guest)) {
      return [];
    }
    const spot_name = entry.spot_name.trim();
    if (spot_name === '' && entry.guest !== 0) {
      return [];
    }
    return [{ spot_name, guest: entry.guest }];
  });
}

export function getAllBookedSpotNames(bookedSpots: BookedSpot[] | undefined): string[] {
  return normalizeBookedSpots(bookedSpots).map((spot) => spot.spot_name);
}

/** Wire sync: includes blank member slot names; drops empty guest slots only. */
export function getWireBookedSpotNames(bookedSpots: BookedSpot[] | undefined): string[] {
  return normalizeBookedSpotsPreservingEmptyMemberSlot(bookedSpots).map((spot) => spot.spot_name);
}

function hasSubstantiveMemberSpot(spots: BookedSpot[]): boolean {
  return getMemberBookedSpotName(spots).trim().length > 0;
}

/** True when `spots` is only an unassigned member slot (`guest === 0`, blank name) — no real data. */
export function isLoneEmptyMemberPlaceholder(spots: BookedSpot[]): boolean {
  return spots.length === 1 && spots[0]?.guest === 0 && spots[0]?.spot_name === '';
}

function shouldPreferCamelOverLoneEmptySnakePlaceholder(
  fromSnake: BookedSpot[],
  fromCamel: BookedSpot[]
): boolean {
  return (
    isLoneEmptyMemberPlaceholder(fromSnake) &&
    fromCamel.length > 0 &&
    (hasSubstantiveMemberSpot(fromCamel) || getGuestSpots(fromCamel).length > 0)
  );
}

/**
 * Merge snake/camel `booked_spots` arrays from the same payload.
 * Explicit empty snake `[]` falls through to camel; a lone empty member placeholder
 * in snake also yields to populated camel (member and/or guest slots).
 */
export function mergeSnakeCamelBookedSpots(snakeRaw: unknown, camelRaw: unknown): BookedSpot[] {
  const fromSnake = normalizeBookedSpotsPreservingEmptyMemberSlot(snakeRaw);
  const fromCamel = normalizeBookedSpotsPreservingEmptyMemberSlot(camelRaw);

  if (fromSnake.length > 0) {
    if (shouldPreferCamelOverLoneEmptySnakePlaceholder(fromSnake, fromCamel)) {
      return fromCamel;
    }
    return fromSnake;
  }

  if (fromCamel.length > 0) {
    return fromCamel;
  }

  return [];
}

export function normalizeBookedSpotNames(bookedSpots: BookedSpot[] | undefined): string[] {
  return getAllBookedSpotNames(bookedSpots);
}

export function getMemberSpot(spots: BookedSpot[] | undefined): BookedSpot | undefined {
  return normalizeBookedSpots(spots).find((spot) => spot.guest === 0);
}

export function getGuestSpots(spots: BookedSpot[] | undefined): BookedSpot[] {
  return normalizeBookedSpots(spots).filter((spot) => spot.guest >= 1);
}

export function getMemberBookedSpotName(bookedSpots: BookedSpot[] | undefined): string {
  return getMemberSpot(bookedSpots)?.spot_name ?? '';
}

/** True when no owner spot (`guest === 0`) has a non-empty name. */
export function isMemberBookedSpotUnassignedFromBookedSpots(
  bookedSpots: BookedSpot[] | undefined
): boolean {
  return getMemberBookedSpotName(bookedSpots).trim().length === 0;
}

export function getMemberVisibleBookedSpotNames(
  bookedSpots: BookedSpot[] | undefined,
  guestBookings?: number
): string[] {
  const normalized = normalizeBookedSpots(bookedSpots);
  if (guestBookings !== undefined && Number(guestBookings) <= 0) {
    return getAllBookedSpotNames(normalized);
  }
  const memberSpotName = getMemberBookedSpotName(normalized);
  return memberSpotName ? [memberSpotName] : [];
}

/** @deprecated Prefer getMemberBookedSpotName — returns the first name in the list, not necessarily the owner. */
export function getPrimaryBookedSpotName(bookedSpots: BookedSpot[] | undefined): string {
  return getAllBookedSpotNames(bookedSpots)[0] ?? '';
}

/** Wire contract: index 0 is the member slot; buddy/guest names follow. */
export const MEMBER_BOOKED_SPOT_INDEX = 0;
